import api from './client'

export interface Contact {
  id: string
  name: string
  note: string | null
  archived: boolean
  owedToUs: number
  weOwe: number
  /** Latest owed-entry or settlement date. Optional: an older backend omits it. */
  lastActivityOn?: string | null
}

export function listContacts(includeArchived = false): Promise<Contact[]> {
  return api
    .get('/contacts', { params: includeArchived ? { includeArchived: true } : {} })
    .then((r) => r.data.contacts)
}

export function createContact(payload: { name: string; note?: string }): Promise<Contact> {
  return api.post('/contacts', payload).then((r) => r.data)
}

export function updateContact(
  id: string,
  payload: { name: string; note?: string }
): Promise<Contact> {
  return api.patch(`/contacts/${id}`, payload).then((r) => r.data)
}

export function archiveContact(id: string): Promise<Contact> {
  return api.post(`/contacts/${id}/archive`).then((r) => r.data)
}

export function unarchiveContact(id: string): Promise<Contact> {
  return api.post(`/contacts/${id}/unarchive`).then((r) => r.data)
}

export function deleteContact(id: string): Promise<void> {
  return api.delete(`/contacts/${id}`)
}

export interface OwedEntry {
  id: string
  direction: 'OWED_TO_US' | 'WE_OWE'
  amount: number
  outstanding: number
  entryDate: string
  description: string
  /** Null for an entry that was already owed before the household used the app. */
  sourceTransactionId: string | null
  sourceType: 'EXPENSE' | 'PERSON_PAYMENT' | 'OPENING'
}

export function listContactEntries(
  contactId: string,
  status: 'open' | 'all' = 'open'
): Promise<OwedEntry[]> {
  return api
    .get(`/contacts/${contactId}/entries`, { params: { status } })
    .then((r) => r.data.entries)
}

export function getOwedSummary(): Promise<{ owedToUs: number; weOwe: number }> {
  return api.get('/owed/summary').then((r) => r.data)
}

export interface SettlementPayload {
  contactId: string
  settledOn: string
  financialAccountId?: string
  allocations: { entryId: string; amount: number }[]
  note?: string
  /** Clear the entries without money moving: given up (owed to you) or forgiven (you owe). */
  writeOff?: boolean
  /** Paid on top of what the allocations clear: owed back the other way, or kept as "Rounded off". */
  extra?: { amount: number; handling: 'OWE_BACK' | 'ROUNDED_OFF' }
}

export interface SettlementResult {
  id: string
  netAmount: number
  direction: 'IN' | 'OUT' | 'EVEN'
  transactionId: string | null
  earmarkWarning?: {
    goals: { id: string; name: string }[]
    shortfall: number
  } | null
}

export function createSettlement(payload: SettlementPayload): Promise<SettlementResult> {
  return api.post('/settlements', payload).then((r) => r.data)
}

export interface SettlementSummary {
  id: string
  settledOn: string
  /** Money that moved; 0 when the two sides offset exactly. */
  netAmount: number
  direction: 'IN' | 'OUT' | 'EVEN' | 'WRITE_OFF'
  transactionId: string | null
  /** Total cleared by a write-off. Optional: an older backend omits it. */
  writtenOff?: number
}

/** Newest first. */
export function listContactSettlements(contactId: string): Promise<SettlementSummary[]> {
  return api.get(`/contacts/${contactId}/settlements`).then((r) => r.data.settlements)
}

export function deleteSettlement(id: string): Promise<void> {
  return api.delete(`/settlements/${id}`)
}

export interface CashEntryPayload {
  contactId: string
  direction: 'LENT' | 'BORROWED'
  amount: number
  /** Omitted when alreadyOwed: no money moves through an account. */
  financialAccountId?: string
  date: string
  description?: string
  alreadyOwed?: boolean
}

export interface CashEntryResult {
  transactionId: string | null
  earmarkWarning?: {
    goals: { id: string; name: string }[]
    shortfall: number
  } | null
}

export function createCashEntry(payload: CashEntryPayload): Promise<CashEntryResult> {
  return api.post('/owed/cash', payload).then((r) => r.data)
}

export function deleteCashEntry(transactionId: string): Promise<void> {
  return api.delete(`/owed/cash/${transactionId}`)
}

export function deleteOpeningEntry(entryId: string): Promise<void> {
  return api.delete(`/owed/opening/${entryId}`)
}

/** Paying for other people and not yourself: lending, never spend. */
export interface PaidForOthersPayload {
  financialAccountId: string
  date: string
  description: string
  shares: { contactId: string; amount: number }[]
  notes?: string
}

export type PaidForOthersResult = CashEntryResult

export function createPaidForOthers(payload: PaidForOthersPayload): Promise<PaidForOthersResult> {
  return api.post('/owed/paid-for-others', payload).then((r) => r.data)
}
